import { APP_NAME } from '$lib/constants';
import { type Writable, writable } from 'svelte/store';
import type { GlobalModelConfig, ModelConfig } from '$lib/apis';
import type { Banner } from '$lib/types';
import type { Socket } from 'socket.io-client';

// Backend
export const WEBUI_NAME = writable(APP_NAME);
export const config: Writable<Config | undefined> = writable(undefined);
export const user: Writable<SessionUser | undefined> = writable(undefined);

// Frontend
export const MODEL_DOWNLOAD_POOL = writable({});
// Dropdown Store
export const selectedOption = writable({});

export const mobile = writable(false);

export const socket: Writable<null | Socket> = writable(null);
export const activeUserCount: Writable<null | number> = writable(null);
export const USAGE_POOL: Writable<null | string[]> = writable(null);

export const theme = writable('system');
export const chatId = writable('');

// Image Settings
export const aspectRatio = writable('16:9');
export const imageResolution = writable('1K');
export const previewMagicHatFeature = writable(true);
export const imageGenEnabled = writable(false);

// Video Settings
export const isVideoGenerationEnabled = writable(false);

// Search Engine
export const searchType = writable('all');
export const sources = writable([
	{"key": "research", "value": "Pubmed, PubChem"},
	{"key": "patents", "value": "Patents"},
	{"key": "wikipedia", "value": "Wiki"},
	{"key": "all", "value": "All"}
])

export const items = writable([]);
export const options = writable({});
export const reportFor30 = writable([]);
export const reportFor7 = writable([]);
export const reportFor1 = writable([]);
export const reportFor180 = writable([]);
export const reportTag = writable([]);
export const chats = writable([]);
export const tags = writable([]);
export const users = writable([]);
export const availablePlatformCredits = writable(0);
export const subscriptionType = writable("Enterprise")

export const models: Writable<Model[]> = writable([]);
export const hiddenModels: Writable<Model[]> = writable([]);
export const modelGroups: Writable<Model[]> = writable([]);
export const prompts: Writable<Prompt[]> = writable([]);
export const documents: Writable<Document[]> = writable([]);
export const iMemoryTables: Writable<Document[]> = writable([]);
export const folders: Writable<Document[]> = writable([]);
export const currentFolder = writable("");
export const tools = writable([]);
export const selectedMemoryForChat = writable([]);
export const selectedSuggestionsForChat = writable([]);

export const banners: Writable<Banner[]> = writable([]);

export const settings: Writable<Settings> = writable({});

export const showSidebar = writable(true);
export const showMagicHat = writable(false);
export const showVenusHat = writable(false);
export const venusHat = writable(false);
export const persona = writable("");
export const personas = writable([
	"Business Analyst : with 20 years experience", 
	"Solution Architect : with 20 years experience", 
	"Data Scientist : with 20 years experience", 
	"Data Analyst : with 20 years experience",
	"Lead Software Engineer",
	"Senior Security Engineer",
	"Director of IT Operations",
	"Lead Software Engineer",
	"Financial Controller",
	"Senior Business Analyst : Finance Systems",
	"Senior Financial Analyst",
	"Lead Researcher (Biotech)",
	"Senior Data Scientist (AI/ML)",
	"Senior R&D Engineer (Robotics)",
]);
export const personaBasedConversation = writable(false);
export const showSettings = writable(false);
export const showVideoSettings = writable(false);
export const showArchivedChats = writable(false);
// export const showChangelog = writable(false);
export const showCallOverlay = writable(false);
export const currentPageSize = writable(50);
export const credits = writable(0);
export const udays = writable(2);
export const fetchingChats = writable(false);

export type Model = OpenAIModel | OllamaModel;

type BaseModel = {
	id: string;
	name: string;
	info?: ModelConfig;
};

export interface OpenAIModel extends BaseModel {
	external: boolean;
	source?: string;
}

export interface OllamaModel extends BaseModel {
	details: OllamaModelDetails;
	size: number;
	description: string;
	model: string;
	modified_at: string;
	digest: string;
}

type OllamaModelDetails = {
	parent_model: string;
	format: string;
	family: string;
	families: string[] | null;
	parameter_size: string;
	quantization_level: string;
};

type Settings = {
	models?: string[];
	conversationMode?: boolean;
	speechAutoSend?: boolean;
	responseAutoPlayback?: boolean;
	audio?: AudioSettings;
	showUsername?: boolean;
	saveChatHistory?: boolean;
	notificationEnabled?: boolean;
	title?: TitleSettings;
	splitLargeDeltas?: boolean;
	chatDirection: 'LTR' | 'RTL';

	system?: string;
	requestFormat?: string;
	keepAlive?: string;
	seed?: number;
	temperature?: string;
	repeat_penalty?: string;
	top_k?: string;
	top_p?: string;
	num_ctx?: string;
	options?: ModelOptions;
};

type ModelOptions = {
	stop?: boolean;
};

type AudioSettings = {
	STTEngine?: string;
	TTSEngine?: string;
	speaker?: string;
	model?: string;
	nonLocalVoices?: boolean;
};

type TitleSettings = {
	auto?: boolean;
	model?: string;
	modelExternal?: string;
	prompt?: string;
};

type Prompt = {
	command: string;
	user_id: string;
	title: string;
	content: string;
	timestamp: number;
};

type Document = {
	collection_name: string;
	filename: string;
	name: string;
	title: string;
};

type Config = {
	status: boolean;
	name: string;
	version: string;
	default_locale: string;
	default_models: string[];
	default_prompt_suggestions: PromptSuggestion[];
	gateway_url: string;
	features: {
		auth: boolean;
		auth_trusted_header: boolean;
		enable_signup: boolean;
		enable_web_search?: boolean;
		enable_image_generation: boolean;
		enable_admin_export: boolean;
		enable_community_sharing: boolean;
	};
};

type PromptSuggestion = {
	content: string;
	title: [string, string];
};

type SessionUser = {
	id: string;
	email: string;
	name: string;
	role: string;
	profile_image_url: string;
};
